import { useSelector } from 'react-redux';

import { Album, FavoriteAlbumData, FeatureItem, Playlist } from 'src/types';
import {
  addFavouriteAlbumToStore,
  addFavouriteContributorToStore,
  addFavouriteTrackToStore,
  addRecentTrack,
  appendFavouriteAlbums,
  appendFavouriteTracks,
  appendMusicForYouList,
  appendSerenityPlaylist,
  appendWisdomPlaylist,
  getMusicReducer,
  RecentTrack,
  removeFavouriteAlbum,
  removeFavouriteContributor,
  removeFavouriteTrack,
  setActivePlaylist,
  setActiveWisdomData,
  setFavouriteAlbums,
  setFavouriteContributors,
  setFavouriteTracks,
  setFeaturedBooks,
  setFeaturedItems,
  setFeaturedMusic,
  setFeaturedSerenity,
  setMusicForYouList,
  setMusicPlaying,
  setPreferredLocale,
  setPreMuteVolume,
  setRepeatMode,
  setSerenityPlaylist,
  setShuffleEnabled,
  setShuffleOrder,
  setVolume,
  setWisdomPlaylist,
  updateAlbumContributorMedia,
  updateAlbumLikedStatus,
  updateTrackLikedStatus,
} from '../../store/reducers/musicStore';
import { AppDispatch, useAppDispatch } from '../../store/store';

export const useMusicState = () => {
  const state = useSelector(getMusicReducer);
  const dispatch: AppDispatch = useAppDispatch();

  return {
    ...state,
    setActivePlaylist: (activePlaylist: Playlist | null) =>
      dispatch(setActivePlaylist(activePlaylist)),
    setMusicPlaying: (musicPlaying: boolean) =>
      dispatch(setMusicPlaying(musicPlaying)),
    setMusicForYouList: (musicList: Album[]) =>
      dispatch(setMusicForYouList(musicList)),
    appendMusicForYouList: (musicList: Album[]) =>
      dispatch(appendMusicForYouList(musicList)),
    setSerenityPlaylist: (serenityList: Album[]) =>
      dispatch(setSerenityPlaylist(serenityList)),
    appendSerenityPlaylist: (serenityList: Album[]) =>
      dispatch(appendSerenityPlaylist(serenityList)),
    setWisdomPlaylist: (wisdomList: Album[]) =>
      dispatch(setWisdomPlaylist(wisdomList)),
    appendWisdomPlaylist: (wisdomList: Album[]) =>
      dispatch(appendWisdomPlaylist(wisdomList)),
    setFeaturedMusic: (musicList: Album[]) =>
      dispatch(setFeaturedMusic(musicList)),
    setFeaturedSerenity: (serenityList: Album[]) =>
      dispatch(setFeaturedSerenity(serenityList)),
    setFeaturedBooks: (booksList: Album[]) =>
      dispatch(setFeaturedBooks(booksList)),
    addRecentTrack: (recentTrack: RecentTrack) =>
      dispatch(addRecentTrack(recentTrack)),
    setActiveWisdomData: (wisdomData: {
      album: Album;
      albumIndex: number;
      itemIndex: number;
    }) => dispatch(setActiveWisdomData(wisdomData)),
    setFeaturedItems: (featuredItems: FeatureItem[]) =>
      dispatch(setFeaturedItems(featuredItems)),
    setShuffleEnabled: (enabled: boolean) =>
      dispatch(setShuffleEnabled(enabled)),
    setShuffleOrder: (order: number[]) => dispatch(setShuffleOrder(order)),
    setRepeatMode: (mode: 'off' | 'all' | 'one') =>
      dispatch(setRepeatMode(mode)),
    setVolume: (volume: number) => dispatch(setVolume(volume)),
    setPreMuteVolume: (volume: number) => dispatch(setPreMuteVolume(volume)),
    setPreferredLocale: (locale: string) =>
      dispatch(setPreferredLocale(locale)),
    updateAlbumLikedStatus: (albumId: string, isLiked: boolean) =>
      dispatch(updateAlbumLikedStatus({ albumId, isLiked })),
    updateTrackLikedStatus: (trackId: string, isLiked: boolean) =>
      dispatch(updateTrackLikedStatus({ trackId, isLiked })),
    updateAlbumContributorMedia: (payload: {
      contributorId: string;
      mediaData: {
        thumbnailUrl?: string;
        videoDesktopUrl?: string;
        videoMobileUrl?: string;
      };
    }) => dispatch(updateAlbumContributorMedia(payload)),
    setFavouriteAlbums: (albums: FavoriteAlbumData[]) =>
      dispatch(setFavouriteAlbums(albums)),
    setFavouriteContributors: (contributors: any[]) =>
      dispatch(setFavouriteContributors(contributors)),
    setFavouriteTracks: (tracks: any[]) => dispatch(setFavouriteTracks(tracks)),
    addFavouriteAlbumToStore: (album: FavoriteAlbumData) =>
      dispatch(addFavouriteAlbumToStore(album)),
    addFavouriteTrackToStore: (track: any) =>
      dispatch(addFavouriteTrackToStore(track)),
    addFavouriteContributorToStore: (contributor: any) =>
      dispatch(addFavouriteContributorToStore(contributor)),
    removeFavouriteAlbum: (albumId: string) =>
      dispatch(removeFavouriteAlbum(albumId)),
    removeFavouriteTrack: (trackId: string) =>
      dispatch(removeFavouriteTrack(trackId)),
    removeFavouriteContributor: (contributorId: string) =>
      dispatch(removeFavouriteContributor(contributorId)),
    appendFavouriteAlbums: (albums: FavoriteAlbumData[]) =>
      dispatch(appendFavouriteAlbums(albums)),
    appendFavouriteTracks: (tracks: any[]) =>
      dispatch(appendFavouriteTracks(tracks)),
  };
};
